// Bill's answers are model-generated and a conversation can be shared by link,
// so any URL in an answer is attacker-controllable text a victim may render.
//
// Deliberately no allowlist of familiar non-Plaid hosts: it would gate nothing,
// and would drop the label on code forges and registries where anyone can
// publish the page being linked to.

const SAFE_SCHEMES = new Set(['https:', 'http:', 'mailto:']);

// A fixed base, not window.location, so a link classifies the same way on the
// server, in dev, and in production.
export const RESOLUTION_BASE = 'https://plaid.com/';

const SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;
const AUTHORITY_PREFIX_PATTERN = /^[/\\][/\\]/;
const CONTROL_AND_WHITESPACE_PATTERN = new RegExp('[\\u0000-\\u0020]', 'g');

export type ClassifiedLink =
  | { kind: 'unsafe' }
  | { kind: 'trusted'; href: string; newTab: boolean }
  | { kind: 'annotated'; href: string; host: string };

const isPlaidHost = (host: string): boolean =>
  host === 'plaid.com' || host.endsWith('.plaid.com');

export const classifyLink = (rawHref: string | undefined): ClassifiedLink => {
  if (!rawHref) {
    return { kind: 'unsafe' };
  }

  // Browsers ignore these when resolving a URL, so a scheme split by a newline
  // still navigates.
  const href = rawHref.replace(CONTROL_AND_WHITESPACE_PATTERN, '');
  if (!href) {
    return { kind: 'unsafe' };
  }

  if (href.startsWith('#')) {
    return { kind: 'trusted', href, newTab: false };
  }

  // Classify by what the parser resolves, never by inspecting the string, so
  // the verdict agrees with however the browser will resolve the href.
  let url: URL;
  try {
    url = new URL(href, RESOLUTION_BASE);
  } catch {
    return { kind: 'unsafe' };
  }

  if (!SAFE_SCHEMES.has(url.protocol)) {
    return { kind: 'unsafe' };
  }

  if (url.protocol === 'mailto:') {
    return { kind: 'trusted', href: url.href, newTab: false };
  }

  const host = url.hostname.toLowerCase();
  if (!isPlaidHost(host)) {
    return { kind: 'annotated', href: url.href, host };
  }

  // Stay relative so the link works on preview deployments and in dev. A
  // backslash delimits the authority like a slash does, so `/\host` is not one.
  const isOriginRelative =
    !SCHEME_PATTERN.test(href) && !AUTHORITY_PREFIX_PATTERN.test(href);

  return {
    kind: 'trusted',
    href: isOriginRelative ? href : url.href,
    newTab: true,
  };
};
